// src/pages/AdjustPayment.jsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./Renew.css"; // same styling as cancel pages
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

export default function AdjustPayment() {
  const mmyyRef = useRef(null);
  const tailRef = useRef(null);
  const amountRef = useRef(null);

  const [mmyy, setMmyy] = useState("");
  const [tail, setTail] = useState("");
  const [view, setView] = useState(null);        // server AdjustPaymentView
  const [amount, setAmount] = useState("");      // textbox value
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEscapeToGoBack();

  const onlyDigits = (s = "") => String(s).replace(/\D/g, "");
  const pad4 = (s = "") => onlyDigits(s).padStart(4, "0");
  const detectSeries = (t) => (onlyDigits(t).length === 5 ? "S" : "B");
  const canonical = (mm, t) => {
    const tailDigits = onlyDigits(t);
    const series = detectSeries(tailDigits);
    const tailNorm = series === "B" ? tailDigits.padStart(4, "0") : tailDigits;
    return `${series}/${pad4(mm)}/${tailNorm}`;
  };

  useEffect(() => { mmyyRef.current?.focus(); }, []);

  const onKeyDownMmyy = (e) => { if (e.key === "Enter") tailRef.current?.focus(); };
  const onKeyDownTail = async (e) => { if (e.key === "Enter") await handleSearch(); };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
  };

  const handleSearch = async () => {
    setError(""); setView(null);
    const mm = pad4(mmyy), t = onlyDigits(tail);
    if (!mm || !t) { setError("Please fill MMYY and Receipt Number."); return; }
    try {
      setBusy(true);
      const res = await axios.get("https://www.lianfoong.com/api/payments/adjust/lookup", {
        params: { mmyy: mm, receiptTail: t },
      });
      setView(res.data);
      setAmount(String(res.data.amount ?? ""));
      setTimeout(() => amountRef.current?.focus(), 0);
    } catch (e) {
      setError(e?.response?.data?.message || "Not found / not renewed or redeemed.");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!view) return;
    const rcpt = view.receiptNo || canonical(mmyy, tail);
    const newAmt = Number(amount);
    if (!(newAmt > 0)) { setError("Amount must be a positive number."); return; }
    try {
      setBusy(true);
      const res = await axios.put("https://www.lianfoong.com/api/payments/adjust", {
        receiptNo: rcpt,
        newAmount: newAmt
      });
      setView(res.data);
      setAmount(String(res.data.amount ?? ""));
      alert("Amount adjusted.");
    } catch (e) {
      setError(e?.response?.data?.message || "Adjustment failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="renew-container">
      <h2 className="title">Adjust Payment</h2>

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          ref={mmyyRef}
          value={mmyy}
          onChange={(e) => setMmyy(onlyDigits(e.target.value))}
          onKeyDown={onKeyDownMmyy}
          placeholder="MMYY"
          inputMode="numeric"
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number :</label>
        <input
          ref={tailRef}
          value={tail}
          onChange={(e) => setTail(onlyDigits(e.target.value))}
          onKeyDown={onKeyDownTail}
          placeholder="0001 or 80001"
          inputMode="numeric"
        />
      </div>

      <button className="search-btn" onClick={handleSearch} disabled={busy}>
        {busy ? "Searching..." : "Search"}
      </button>
      {error && <p className="error-message">{error}</p>}

      {view && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Type:</strong> {view.kind}</p>
            <p><strong>Receipt:</strong> {view.receiptNo}</p>
            {view.newReceiptNo && <p><strong>New Receipt:</strong> {view.newReceiptNo}</p>}
            <p><strong>Principal:</strong> {view.principal != null ? `$${Number(view.principal).toFixed(2)}` : "-"}</p>
            <p><strong>Grant Date:</strong> {fmtDate(view.grantDate)}</p>
            <p><strong>Expiry Date:</strong> {fmtDate(view.expiryDate)}</p>
            <p><strong>Item:</strong> {view.item}</p>
            <p><strong>Weight:</strong> {view.weight}</p>
            <p><strong>{view.kind === "RENEWAL" ? "Payment Date" : "Redeem Date"}:</strong> {fmtDate(view.when)}</p>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label>{view.kind === "RENEWAL" ? "Renewal Payment Amount" : "Redeem Amount"}</label>
            <input
              ref={amountRef}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              inputMode="decimal"
            />
          </div>

          <div className="alert-box" style={{ marginTop: 12 }}>
            <button disabled={busy} onClick={handleSubmit}>
              {busy ? "Saving..." : "Save Adjustment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
